export const SECTIONS = [
  { key: "stories", label: "Family Stories" },
  { key: "history", label: "Family History" },
  { key: "attractions", label: "Landmarks & Attractions" },
  { key: "news", label: "News & Milestones" },
];

export function formatDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

// Multi-part pieces belong together.
//
// "E.I. In Love" is three letters, "BOOM Goes the Brandywine" is three entries,
// "Little Alexis" is two. Sorted by date they scatter across the grid and read
// as unrelated fragments, which is how the first version looked. This pulls each
// set back into one band, in reading order.
//
// The separator must be followed by an actual part word. Without that,
// "Cousin to Cousin Event at Hagley | Sept. 28, 2017" and "Memorial Day Flag
// Event | May 23, 2018" would be mistaken for series, because the pipe is doing
// a different job in those titles.
const SERIES_PATTERN =
  /^(.*?)\s*[|!:–-]\s*(?:Letter|Entry|Part|Chapter)\s*(?:No\.?\s*)?(\d+)\s*$/i;

export function parseSeries(title) {
  const m = SERIES_PATTERN.exec(title || "");
  if (!m) return null;
  const name = m[1].trim();
  if (!name) return null;
  return { name, part: parseInt(m[2], 10) };
}

// Strip a leading article so "The Family Trip" files under F, not T -- the way
// a library would shelve it.
function sortKey(s) {
  return (s || "").replace(/^(the|a|an)\s+/i, "").toLowerCase();
}

/**
 * Split articles into ordered series bands plus the remaining singles.
 * Returns { series: [{ name, articles }], singles: [...] }, both alphabetical,
 * with each series' own articles in part order.
 */
export function groupArticles(articles) {
  const bySeries = new Map();
  const singles = [];

  (articles || []).forEach((a) => {
    const s = parseSeries(a.title);
    if (!s) {
      singles.push(a);
      return;
    }
    if (!bySeries.has(s.name)) bySeries.set(s.name, []);
    bySeries.get(s.name).push({ ...a, __part: s.part });
  });

  const series = [];
  bySeries.forEach((items, name) => {
    // One lonely part is not a series -- it reads better as a normal card.
    if (items.length < 2) {
      singles.push(items[0]);
      return;
    }
    items.sort((x, y) => x.__part - y.__part);
    series.push({ name, articles: items });
  });

  series.sort((x, y) => sortKey(x.name).localeCompare(sortKey(y.name)));
  singles.sort((x, y) => sortKey(x.title).localeCompare(sortKey(y.title)));
  return { series, singles };
}


/**
 * Sections with no multi-part series (News, History, Attractions) were one flat
 * grid of everything. Banding them by year gives the eye somewhere to rest and
 * makes "when" legible at a glance, which is most of what News is for.
 * Newest year first; within a year, newest first.
 */
export function groupByYear(articles) {
  const bands = new Map();
  (articles || []).forEach((a) => {
    const raw = a.original_published_at || a.created_at;
    const d = raw ? new Date(raw) : null;
    const year = d && !isNaN(d) ? String(d.getFullYear()) : "Undated";
    if (!bands.has(year)) bands.set(year, []);
    bands.get(year).push(a);
  });

  const out = [];
  bands.forEach((items, year) => {
    items.sort((x, y) => {
      const dx = new Date(x.original_published_at || x.created_at);
      const dy = new Date(y.original_published_at || y.created_at);
      return dy - dx;
    });
    out.push({ name: year, articles: items });
  });

  // Newest year first; anything undated sits at the end rather than sorting as 0.
  out.sort((x, y) => {
    if (x.name === "Undated") return 1;
    if (y.name === "Undated") return -1;
    return Number(y.name) - Number(x.name);
  });
  return out;
}


/**
 * Landmarks & Attractions is about PLACES, not about when a post was written.
 * Sorting it by date put a 2017 news item about a fountain reopening in the
 * middle of the two directories, which reads as a shuffle rather than a section.
 *
 * Order by what a thing IS:
 *   1. the directories  — articles that actually contain landmarks
 *   2. the guides       — long-form pieces about the places
 *   3. the notices      — short dated items, newest first
 */
export function orderPlaces(articles) {
  const rank = (a) => {
    const blocks = a.blocks || a.body_blocks || [];
    const hasLandmarks = blocks.some(
      (b) => b && (b.type === "landmark" || b.type === "landmarkIndex")
    );
    if (hasLandmarks) return 0;
    // Card payloads omit the body, so fall back to length as a proxy for a guide.
    const long = (a.subtitle || "").length > 90;
    return long ? 1 : 2;
  };
  const KIND = ["Directory", "Guides", "Announcements"];
  const bands = [[], [], []];
  (articles || []).forEach((a) => bands[rank(a)].push(a));
  bands[2].sort((x, y) => {
    const dx = new Date(x.original_published_at || x.created_at);
    const dy = new Date(y.original_published_at || y.created_at);
    return dy - dx;
  });
  return bands
    .map((items, i) => ({ name: KIND[i], articles: items }))
    .filter((b) => b.articles.length > 0);
}

/**
 * The byline under a piece.
 *
 * The salvaged WordPress data carries three different values for what is really
 * one author: "du Pont Family Genealogy Office" (21 pieces), "Genealogy Office"
 * (5) and a raw login name (3). Sam, 2026-09-17: the date and poster should read
 * clearly on every post.
 *
 * Normalised on READ, exactly like the retired scrapbook categories — the stored
 * value is never rewritten, so nothing in the family's records is altered to
 * tidy a label.
 */
const OFFICE = "du Pont Family Genealogy Office";

const BYLINES = {
  "genealogy office": OFFICE,
  "du pont family genealogy office": OFFICE,
};

export function bylineFor(author) {
  const raw = String(author || "").trim();
  if (!raw) return "";
  const known = BYLINES[raw.toLowerCase()];
  if (known) return known;
  // A bare login name — no spaces, no capitals — is not a person's name and
  // should not be printed as one. Until Sam says who it belongs to, the piece
  // carries the office byline it was published under.
  if (!/\s/.test(raw) && raw === raw.toLowerCase()) return OFFICE;
  return raw;
}
